<script setup lang="ts">
import { cn } from '@pkg/ui/lib/utils';
import { Breadcrumbs } from '@pkg/ui/components/ui/breadcrumbs';
import { BagHeader } from '@pkg/ui/components/ui/bag-header';
import CalendarIcon from '@pkg/cms-icons/icons/Calendar-Month_Icon.svg';
import ChipSelectUi from '@pkg/ui/components/ui/chip/chip-select/ChipSelectUi.vue';
import { Modal } from '@pkg/ui/components/ui/modal';
import { logger } from '@pkg/logger';
import {
  getPrice,
  isBagAvailable,
  getPickupTag,
  getCollectionWindowLabel,
  getRatingSubtitle,
  getTitle,
  getItemCategoryLabel,
  getItemSubCategoryDetails,
  getItemCategoryV2Details,
  getItemTagBadges,
} from '@pkg/utilities/surprisebag';
import { substitutePlainTextVariables } from '@pkg/utilities/strings';
import type { SurpriseBag } from '@pkg/types/surpriseBag';
import { ampli } from '@pkg/analytics-helper/src/ampli';
import { surpriseBagSeoKeyword } from '../../../../../sanity/schemas/utils/slugs/slugConstants';
import { buildDeepLink } from '../../../helpers/surpriseBagHelper';
import SurpriseBagPrice from './SurpriseBagPrice.vue';
import SurpriseBagCollectionTime from './SurpriseBagCollectionTime.vue';
import SurpriseBagLocation from './SurpriseBagLocation.vue';
import { SurpriseBagRating, SurpriseBagQR, SurpriseBagNearbyBags } from './components/index';
import SurpriseBagMap from './components/SurpriseBagMap.vue';
import { useBuildTimeSanityContent } from '~/app/composables/useBuildTimeSanityContent';
import { useSearchParams } from '~/app/composables/useSearchParams';
import { useSurpriseBagBreadcrumbJsonLd } from '~/app/composables/useSurpriseBagBreadcrumbJsonLd';
import Nudge from '~/app/components/blocks/Nudge.vue';
import SocialsShareGroup from '~/app/components/blocks/SocialsShareGroup.vue';
import { brandProfilePageUrl } from '~/app/features/brandProfile/utils/brandProfileUrls';
import type { AvailabilityBagResponse } from '~/server/api/surprise-bags/uncached/[id]';

const props = defineProps<{
  content: {
    bag: any;
    brandProfileLink?: { name: string; profileSlug: string; language: string } | null;
    nearbyBags?: SurpriseBag[] | null;
  };
}>();

const { locale, t } = useI18n();
const localePath = useLocalePath();

// Subcategory first, falling back to the broader category when a bag has no
// subcategory details. Null when neither is present, so no chip is rendered
// rather than one labelled with the raw enum.
const categoryChip = computed(() => {
  const item = props.content?.bag?.item;
  return getItemSubCategoryDetails(item) ?? getItemCategoryV2Details(item);
});

const device = useDevice();
const nuxtApp = useNuxtApp();

const isDesktop = computed(() => device.isDesktop);

const staticTranslations = await useBuildTimeSanityContent(locale.value);
const { buildPathWithSearchParams } = useSearchParams();

const breadcrumbPath = computed(() => {
  const basePath = locale.value ? `/${locale.value}/${surpriseBagSeoKeyword}` : `/${surpriseBagSeoKeyword}`;
  return buildPathWithSearchParams(basePath);
});

const breadcrumbs = computed(() => [
  { name: t('home'), path: localePath('/'), isLast: false },
  { name: staticTranslations?.mainBreadcrumbText?.toLowerCase() || surpriseBagSeoKeyword, path: breadcrumbPath.value, isLast: false },
  { name: props.content?.bag?.displayName || '', path: '', isLast: true },
]);

useSurpriseBagBreadcrumbJsonLd(
  computed(() => props.content?.bag),
  computed(() => staticTranslations?.mainBreadcrumbText || 'Find Food'),
);

const availabilityResponse = ref<AvailabilityBagResponse | null>(null);

const availabilityItemPrice = computed(() => {
  return (availabilityResponse.value as { itemPrice?: unknown } | null)?.itemPrice ?? null;
});

const availabilityItemValue = computed(() => {
  return (availabilityResponse.value as { itemValue?: unknown } | null)?.itemValue ?? null;
});

const isAvailable = computed(() => {
  const availabilityData = availabilityResponse.value as {
    itemsAvailable?: number | null;
    inSalesWindow?: boolean | null;
    pickupInterval?: unknown | null;
  } | null;

  return isBagAvailable(
    availabilityData?.itemsAvailable ?? props.content?.bag?.itemsAvailable,
    availabilityData?.inSalesWindow ?? props.content?.bag?.inSalesWindow,
    availabilityData?.pickupInterval ?? props.content?.bag?.pickupInterval,
  );
});

const currentPriceProps = computed(() => ({
  availability: isAvailable.value,
  oldPrice: getPrice(
    availabilityItemValue.value ?? props.content?.bag?.item?.itemValue,
    locale.value),
  currentPrice: getPrice(
    availabilityItemPrice.value ?? props.content?.bag?.item?.itemPrice,
    locale.value,
  ),
}));

const fullItemLabel = computed(() => {
  return `${props?.content?.bag?.displayName} - ${props?.content?.bag?.store?.storeLocation?.address?.country?.name} - Too Good To Go`;
});

const displayTitle = computed(() => {
  return (
    getTitle(
      props.content?.bag?.store?.storeName,
      props.content?.bag?.store?.branch,
    ) || props.content?.bag?.displayName || ''
  );
});

const brandProfileHref = computed(() => {
  const profileSlug = props.content?.brandProfileLink?.profileSlug?.trim();
  const brandLocale = props.content?.brandProfileLink?.language?.trim();
  if (!profileSlug || !brandLocale) {
    return null;
  }
  return brandProfilePageUrl(brandLocale, profileSlug);
});

const brandProfileLinkLabel = computed(() => {
  const brandName = props.content?.brandProfileLink?.name?.trim();
  const template = staticTranslations?.brandProfileLinkLabel?.trim() || t('brandProfileLinkLabel', { brand: brandName });
  if (!brandName || !template) {
    return null;
  }
  return substitutePlainTextVariables(template, { brand: brandName });
});

const averageOverallRating = computed(() => {
  const value = props.content?.bag?.item?.averageOverallRating?.averageOverallRating || 0;
  return Number(value.toFixed(1));
});

const collectionTimeProps = computed(() => ({
  timeObject: getCollectionWindowLabel(
    availabilityResponse.value?.pickupInterval?.start ?? props.content?.bag?.pickupInterval?.start,
    availabilityResponse.value?.pickupInterval?.end ?? props.content?.bag?.pickupInterval?.end,
    staticTranslations?.timingTexts?.otherDayText,
  ),
  timeTag: t(getPickupTag(props.content?.bag?.pickupInterval?.start, props.content?.bag?.store?.storeLocation?.address?.country?.isoCode)),
  availableBag: isAvailable.value,
}));

const itemTags = computed(() => {
  return availabilityResponse.value?.itemTags ?? props.content?.bag?.itemTags;
});

const bagHeaderProps = computed(() => ({
  image: props.content?.bag?.item?.coverPicture?.currentUrl || undefined,
  logo: props.content?.bag?.item?.logoPicture?.currentUrl || undefined,
  ratings: averageOverallRating.value || props?.content?.bag?.item?.averageOverallRating?.averageOverallRating || 0,
  altText: fullItemLabel.value,
  availableBag: isAvailable.value,
  logoAltText: props.content?.bag?.item?.logoPicture?.altText || '',
  tags: getItemTagBadges(itemTags.value),
  tagModalCtaText: staticTranslations?.popUpCtaLabel || '',
}));

const modalProps = computed(() => ({
  popUpLink: staticTranslations?.ingredientsCtaLabel || '',
  title: staticTranslations?.popUpTitle || '',
  description: staticTranslations?.popUpDescription || '',
  ctaText: staticTranslations?.popUpCtaLabel || '',
}));

const ratingProps = computed(() => ({
  ratingTotal: averageOverallRating.value,
  ratingTitle: staticTranslations?.ratingTitle || '',
  ratingSubtitle: getRatingSubtitle(
    staticTranslations?.ratingSubtitle,
    props?.content?.bag?.item?.averageOverallRating?.ratingCount,
    props?.content?.bag?.item?.averageOverallRating?.monthCount,
  ),
  ratings: [
    {
      name: staticTranslations?.firstRatingTitle || '',
      value: (props?.content?.bag?.item?.averageOverallRating?.averageCollectionExperienceRating || 0).toFixed(1),
    },
    {
      name: staticTranslations?.secondRatingTitle || '',
      value: (props?.content?.bag?.item?.averageOverallRating?.averageFoodQualityRating || 0).toFixed(1),
    },
    {
      name: staticTranslations?.thirdRatingTitle,
      value: (props?.content?.bag?.item?.averageOverallRating?.averageContentsVarietyRating || 0).toFixed(1),
    },
    {
      name: staticTranslations?.fourthRatingTitle || '',
      value: (props?.content?.bag?.item?.averageOverallRating?.averageFoodQuantityRating || 0).toFixed(1),
    },
  ],
}));

const qrProps = computed(() => ({
  qrTitle: staticTranslations?.qrTitle || '',
  qrSubtitle: staticTranslations?.qrSubtitle || '',
  deepLink: decodeURIComponent(qrDeepLink),
  altText: fullItemLabel.value,
  nudge: staticTranslations?.nudge,
  isDesktop: isDesktop.value,
}));

const mapData = computed(() => ({
  address: props?.content?.bag?.pickupLocation?.address?.addressLine || '',
  coordinates: props?.content?.bag?.pickupLocation?.location,
  title: staticTranslations?.mapSectionTitle || 'Location',
  errorText: staticTranslations?.mapErrorLabel || 'Map error',
  storeName: props?.content?.bag?.store?.storeName || '',
}
));

const deepLinkParams = {
  itemId: props?.content?.bag?.item?.itemId || '',
  itemName: props?.content?.bag?.item?.itemName || '',
  city: props?.content?.bag?.store?.storeLocation?.address?.city || '',
  displayName: props?.content?.bag?.displayName || '',
  category: getItemCategoryLabel(props?.content?.bag?.item),
  qrCode: 'false',
};

const deepLink = buildDeepLink(deepLinkParams);
const qrDeepLink = buildDeepLink({ ...deepLinkParams, qrCode: 'true' });

const mobileSaveSurpriseBagHandler = () => {
  ampli.track({
    event_type: 'reserve_from_website_mobile',
    user_properties: {
      country_iso_code: props?.content?.bag?.store?.storeLocation?.address?.country?.isoCode || '',
      item_id: props?.content?.bag?.item?.itemId || '',
      store_id: props?.content?.bag?.store?.storeId || '',
    },
  });
  window.location.href = deepLink;
};

const isShowRatingSection = computed(() => {
  return props?.content?.bag?.showRatingSection ||
  (
    props.content?.bag?.item?.averageOverallRating?.ratingCount &&
    props.content?.bag?.item?.averageOverallRating?.ratingCount >= 10
  );
});

onMounted(async() => {
  try {
    if (!import.meta.client) { return; }

    const availabilityId = props.content?.bag?.item?.itemId ||
      props.content?.bag?.itemId;

    if (availabilityId) {
      availabilityResponse.value = await $fetch(`/api/surprise-bags/uncached/${availabilityId}`, {
        query: { locale: locale.value },
      });
    }

    await nuxtApp.$ampliReady;
    ampli.track({
      event_type: 'item_viewed',
      user_properties: {
        store_id: props?.content?.bag?.store?.storeId || '',
        item_id: props?.content?.bag?.item?.itemId || '',
        sold_out: !isAvailable.value,
        country_iso_code: props?.content?.bag?.store?.storeLocation?.address?.country?.isoCode || '',
      },
    });
  } catch (e) {
    logger.error('[SurpriseBag] - Error tracking item viewed: ', e);
  }
});

</script>

<template>
  <article :id="content?.bag?.item?.itemId" class="s-detail__wrapper">
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <section class="s-hero">
      <BagHeader v-bind="bagHeaderProps" />
    </section>
    <div class="s-detail__content__wrapper">
      <section :class="cn('s-detail__content', { 's-detail__content__margin': isDesktop })">
        <header>
          <div class="s-detail__title__wrapper">
            <h1 class="s-detail__title">
              {{ displayTitle }}
            </h1>
            <SurpriseBagPrice
              v-if="isDesktop"
              v-bind="currentPriceProps"
            />
          </div>
          <p class="s-detail__subtitle">
            {{ staticTranslations?.surpriseBagKeyword || '' }}
          </p>
          <SurpriseBagCollectionTime
            v-if="isAvailable"
            v-bind="collectionTimeProps"
          />
          <SurpriseBagLocation :address="content?.bag?.store?.storeLocation?.address?.addressLine || ''" />
          <NuxtLink
            v-if="brandProfileHref && brandProfileLinkLabel"
            :to="brandProfileHref"
            class="s-detail__subtitle__link"
          >
            {{ brandProfileLinkLabel }}
          </NuxtLink>
        </header>

        <section v-if="!isAvailable" class="pt-32">
          <p class="s-detail__reminder">
            <img :src="CalendarIcon" alt="Calendar icon"></img>
            <span class="pl-8">{{ staticTranslations?.reminderText || '' }}</span>
          </p>
        </section>

        <section v-if="!isDesktop && staticTranslations?.nudge" class="pt-24">
          <h2 class="s-detail__title__secondary mb-8">
            {{ staticTranslations?.nudge?.nudgeTitle || '' }}
          </h2>
          <Nudge :steps="staticTranslations?.nudge?.steps || []" />
        </section>

        <section class="pt-24">
          <h2 class="s-detail__title__secondary">
            {{ staticTranslations?.descriptionTitle }}
          </h2>
          <p>
            {{ content?.bag?.item?.description || '' }}
          </p>
          <div v-if="categoryChip" class="s-detail__categories">
            <ChipSelectUi
              as="span"
              :interactive="false"
              :leading-avatar-src="categoryChip.iconUrlLight"
            >
              {{ categoryChip.name }}
            </ChipSelectUi>
          </div>
        </section>
        <hr class="s-divider">

        <section v-if="!isDesktop" class="s-detail__header__price--mobile">
          <SurpriseBagPrice
            v-bind="currentPriceProps"
          />
          <button class="s-detail__cta" @click="mobileSaveSurpriseBagHandler">
            {{ staticTranslations?.ctaLabel || '' }}
          </button>
        </section>

        <hr v-if="!isDesktop" class="s-divider">

        <section>
          <h2 class="s-detail__title__secondary">
            {{ staticTranslations?.moreInfoTitle || '' }}
          </h2>
          <p>{{ staticTranslations?.moreInfoContent || '' }}</p>
        </section>
        <hr class="s-divider">
        <Modal
          v-bind="modalProps"
        />
        <hr class="s-divider">

        <section v-if="isShowRatingSection">
          <SurpriseBagRating v-bind="ratingProps" />
        </section>

        <hr v-if="isDesktop && isShowRatingSection" class="s-divider">
      </section>

      <section v-if="isDesktop" class="s-qr s-qr__wrapper">
        <SurpriseBagQR v-bind="qrProps" />
        <div class="mt-24">
          <SurpriseBagMap :map-data="mapData" />
        </div>
      </section>
    </div>
    <section v-if="!isDesktop">
      <SurpriseBagMap :map-data="mapData" />
      <hr class="s-divider">
    </section>
    <SurpriseBagNearbyBags
      :bags="content?.nearbyBags"
      :title="staticTranslations?.nearbyBagsLabel"
    />
    <div class="pt-[64px]">
      <SocialsShareGroup />
    </div>
  </article>
</template>

<style>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-detail__title__secondary {
  @mixin h5-tenon;
  font-weight: bold;
  padding-bottom: theme('spacing.8');
}

.s-divider {
  border-top: 1px solid theme('colors.neutral.30');
  margin:  theme('spacing.24') theme('spacing.0');

  &__sm {
    display: flex;

    @screen tablet-wide {
      display: none;
    }
  }
}

</style>
<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-hero {
  text-align: center;
  padding: 24px 0px;

  :deep(.bag-header__image__wrapper) {
    background-color: var(--theme-background-color-app);
    max-width: 100%;
    min-height: 220px;

    @screen tablet-wide {
      height: 280px;
    }
  }
}

.s-detail {
  &__cta {
    @mixin cta-app;
    margin-top: theme('margin.16');
  }

  &__categories {
    display: flex;
    flex-wrap: wrap;
    gap: theme('spacing.8');
    list-style: none;
    margin-top: theme('margin.16');
    padding: 0;
  }

  &__wrapper {
    background-color: white;
    @mixin body-large;
    color: var(--theme-text-color-app);
    width: 100%;
    padding: theme('spacing.20');
    text-align: left;

    @screen tablet-wide {
      padding: theme('spacing.20') theme('spacing.96');
    }
    @screen desk {
      padding: theme('spacing.40') theme('spacing.96');
    }
    @screen big-desk {
      padding: theme('spacing.20') theme('spacing.96');
    }
  }

  &__content {
    flex: 2.2 1;

    &__margin {
      @screen tablet-wide {
        margin-right: theme('spacing.96');
      }
    }

    &__wrapper {
      display: flex;
      flex-direction: column;

      @screen tablet-wide {
        flex-direction: row;
        justify-content: space-between;
      }
    }
  }

  &__tag {
    &-grey {
      margin-top: theme('margin.16');
      background-color: theme('colors.neutral.20');
    }
  }

  &__reminder {
    background-color: theme('colors.neutral.20');
    padding: theme('spacing.8');
    border-radius: theme('borderRadius.md');
    display: flex;

    @screen tablet-wide {
      width: fit-content;
    }
  }

  &__header {
    display: flex;
    flex-basis: 100%;
  }

  &__subtitle {
    color: var(--theme-heading-color-app);

    &__link {
      display: inline-block;
      text-decoration: underline;
      margin-top: theme('spacing.16');
      color: var(--theme-heading-color-app);
    }
  }

  &__title {
    @mixin h3-tenon;
    font-weight: bold;
    flex: 75%;

    &__wrapper {
      display: flex;
      padding-bottom: theme('spacing.8');
      flex-direction: column;
      align-items: flex-start;

      @screen desk {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
    }
  }
}

.s-link {
  color: var(--theme-heading-color-app);
  text-decoration: underline;
}

.s-qr {
  display: flex;
  flex-direction: column;

  @screen tablet-wide {
    display: flex;
    width: 80%;
    height: 50%;
  }

  &__desktop-sm {
    display: flex;
    @screen tablet {
      display: none;
    }
  }

  &__wrapper {
    @screen tablet {
      flex: 1 1;
      display: flex;
      flex-direction: column;
    }

    @screen tablet-wide {
      max-width: 40%;
    }

    @screen desk {
      max-width: 29%;
    }
  }
}
</style>
